<template>
    <div class="annotation-viewer">

        <!-- 顶部工具栏 -->
        <div class="toolbar">
            <template v-if="!viewMode">
                <button class="tool-btn" :class="{ active: activeTool === 'hand' }" @click="selectTool('hand')">✋</button>
                <button class="tool-btn" :class="{ active: activeTool === 'circle' }" @click="selectTool('circle')">⭕</button>
                <button class="tool-btn" :class="{ active: activeTool === 'rectangle' }" @click="selectTool('rectangle')">▢</button>
                <button class="tool-btn" :class="{ active: activeTool === 'arrow' }" @click="selectTool('arrow')">➚</button>
                <button class="tool-btn" :class="{ active: activeTool === 'pen' }" @click="selectTool('pen')">✏️</button>
                <button class="tool-btn" :class="{ active: activeTool === 'comment' }" @click="selectTool('comment')">💬</button>
                <button class="tool-btn" @click="undo" :disabled="shapes.length === 0">↩️</button>
                <span class="separator"></span>
                <label class="color-label" title="线条颜色">
                    <input type="color" class="color-picker" :value="currentColor" @input="onColorChange" />
                </label>
                <button class="tool-btn delete-btn" @click="deleteSelected" :disabled="selectedShapeIndex < 0 && !selectedAnnoId" title="删除选中元素">🗑️</button>
            </template>
            <template v-else>
                <span class="view-mode-tip">只读模式</span>
            </template>
        </div>

        <!-- 主画布区域 -->
        <div
            class="canvas-container"
            ref="canvasContainer"
            :class="{ 'hand-tool': activeTool === 'hand' }"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseUp"
            @wheel.prevent="handleMouseWheel"
        >
            <!-- 变换层（包含平移、缩放和旋转） -->
            <div class="transform-layer" :style="transformLayerStyle">
                <!-- PDF 渲染 -->
                <canvas v-show="pdfDoc" ref="pdfCanvas" class="base-image"></canvas>

                <!-- 绘图层 -->
                <canvas ref="drawCanvas" class="draw-canvas"></canvas>

                <!-- 批注标记点 + 悬浮框 -->
                <div
                    v-for="anno in annotations"
                    :key="anno.id"
                    class="annotation-marker"
                    :class="{ 'marker-selected': selectedAnnoId === anno.id, 'marker-new': isNewAnno(anno), 'marker-end': anno.status === 'End' }"
                    :style="getAnnotationStyle(anno)"
                >
                    <div class="marker-icon" :style="{ transform: 'scale(' + scale + ')', transformOrigin: '0 0' }" @mousedown.stop="startMarkerDrag(anno, $event)"></div>

                    <div
                        class="popup-card"
                        :class="{ active: activePopup === anno.id }"
                        :style="{ left: anno.popupOffsetX || '30px', top: anno.popupOffsetY || '-10px' }"
                        @mousedown.stop
                        @click.stop
                    >
                        <div class="popup-header">
                            <input class="popup-title-input" v-model="anno.title" :readonly="anno._historical || viewMode" placeholder="输入标题" />
                            <button class="close-btn" @click.stop="togglePopup(anno.id)">×</button>
                        </div>
                        <div class="popup-content">
                            <textarea class="popup-content-textarea" v-model="anno.content" :readonly="anno._historical || viewMode" placeholder="输入批注内容"></textarea>
                        </div>
                        <div v-if="viewMode && replyingAnnoId === anno.id" class="reply-input-area">
                            <textarea class="reply-textarea" v-model="replyCache" placeholder="输入回复内容" @input="onReplyInput(anno)"></textarea>
                            <div class="reply-actions">
                                <button class="save-reply-btn" @click.stop="saveReply(anno)">保存回复</button>
                                <button class="cancel-reply-btn" @click.stop="cancelReply">取消</button>
                            </div>
                        </div>
                        <div v-if="anno.replyContent && replyingAnnoId !== anno.id" class="popup-reply">
                            <span class="reply-label">回复：</span>
                            <span class="reply-text">{{ anno.replyContent }}</span>
                        </div>
                        <div class="popup-footer">
                            <span class="author">{{ anno.author }}</span>
                            <span v-if="viewMode && anno.replyContent && anno.status !== 'End' && replyingAnnoId !== anno.id" class="edit-reply-btn" @click.stop="editReply(anno)">编辑</span>
                            <span v-if="viewMode && !anno.replyContent && anno.status !== 'End' && replyingAnnoId !== anno.id" class="reply-btn" @click.stop="startReply(anno)">回复</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 右下角：缩放 + 旋转按钮 -->
        <div class="zoom-controls">
            <button class="zoom-btn" @click="zoomIn">+</button>
            <div class="zoom-level">{{ zoom }}%</div>
            <button class="zoom-btn" @click="zoomOut">−</button>
            <div class="zoom-separator"></div>
            <button class="zoom-btn" @click="rotateCycle" title="旋转"><span class="rotate-icon" :style="{ transform: 'rotate(' + rotation + 'deg)' }">↻</span></button>
            <button class="zoom-btn" @click="resetRotation" :disabled="rotation === 0" title="重置旋转">⟲</button>
        </div>

        <!-- 左下角：缩略图 -->
        <div class="thumbnail-container">
            <div class="thumb-wrapper" ref="thumbWrapper" v-if="thumbnailUrl" :style="thumbWrapperStyle">
                <img :src="thumbnailUrl" alt="缩略图" class="thumbnail" />
                <div class="thumb-indicator" v-if="indicatorStyle" :style="indicatorStyle"
                     @mousedown.prevent="onThumbIndicatorDown">
                    <div class="thumb-resize-handle thumb-resize-nw" @mousedown.prevent.stop="onThumbResizeStart($event, 'nw')"></div>
                    <div class="thumb-resize-handle thumb-resize-ne" @mousedown.prevent.stop="onThumbResizeStart($event, 'ne')"></div>
                    <div class="thumb-resize-handle thumb-resize-sw" @mousedown.prevent.stop="onThumbResizeStart($event, 'sw')"></div>
                    <div class="thumb-resize-handle thumb-resize-se" @mousedown.prevent.stop="onThumbResizeStart($event, 'se')"></div>
                    <div class="thumb-resize-handle thumb-resize-n" @mousedown.prevent.stop="onThumbResizeStart($event, 'n')"></div>
                    <div class="thumb-resize-handle thumb-resize-s" @mousedown.prevent.stop="onThumbResizeStart($event, 's')"></div>
                    <div class="thumb-resize-handle thumb-resize-w" @mousedown.prevent.stop="onThumbResizeStart($event, 'w')"></div>
                    <div class="thumb-resize-handle thumb-resize-e" @mousedown.prevent.stop="onThumbResizeStart($event, 'e')"></div>
                </div>
            </div>
        </div>

        <!-- 底部：分页控件 -->
        <div class="pagination">
            <button class="page-btn" @click="prevPage" :disabled="currentPage === 1">&lt;</button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button class="page-btn" @click="nextPage" :disabled="currentPage === totalPages">&gt;</button>
        </div>
    </div>
</template>

<script>
import {mapState} from "vuex";
import { deleteAuditMarkupComment } from "@/api/audit";

export default {
    name: "AnnotationViewer",
    props: {
        baseImageUrl: { type: String, default: "" },
        currentPage: { type: Number, default: 1 },
        totalPages: { type: Number, default: 0 },
        zoom: { type: Number, default: 100 },
        loginName: { type: String, default: "" },
        userName: { type: String, default: "" },
        userId: { type: String, default: "" },
        pageId: { type: String, default: "" },
        activeAnnoId: { type: String, default: "" },
        viewMode: { type: Boolean, default: false },
    },
    data() {
        return {
            pdfDoc: null,
            activePopup: null,
            thumbnailUrl: "",
            thumbnailWidth: 0,
            thumbnailHeight: 0,
            thumbDragging: false,
            thumbDragStart: null,
            thumbTranslateStart: null,
            thumbResizing: false,
            thumbResizeDir: '',
            thumbResizeStart: null,
            activeTool: 'hand',
            isDragging: false,
            isDrawing: false,
            dragStartX: 0,
            dragStartY: 0,
            drawStartX: 0,
            drawStartY: 0,
            translateX: 0,
            translateY: 0,
            rotation: 0,
            shapes: [],
            currentPoints: [],
            annotations: [],
            pdfViewport: null,
            currentColor: '#ff0000',
            selectedShapeIndex: -1,
            selectedAnnoId: '',
            draggingAnno: null,
            dragStartPdf: null,
            mouseStart: null,
            isClick: false,
            replyingAnnoId: '',
            replyCache: '',
        };
    },
    computed: {
        ...mapState("auth", ["user"]),
        transformLayerStyle() {
            return {
                transform: `translate(${this.translateX}px, ${this.translateY}px)`,
                transformOrigin: '0 0',
            };
        },
        scale() {
            return this.zoom / 100;
        },
        indicatorStyle() {
            if (!this.pdfViewport || !this.thumbnailWidth || !this.thumbnailHeight) return null;
            const pdfCanvas = this.$refs.pdfCanvas;
            const container = this.$refs.canvasContainer;
            if (!pdfCanvas || !container) return null;

            const dpr = window.devicePixelRatio || 1;
            const canvasW = pdfCanvas.width / dpr;
            const canvasH = pdfCanvas.height / dpr;
            const contW = container.offsetWidth;
            const contH = container.offsetHeight;

            const vLeft = Math.max(0, -this.translateX);
            const vTop = Math.max(0, -this.translateY);
            const vRight = Math.min(canvasW, -this.translateX + contW);
            const vBottom = Math.min(canvasH, -this.translateY + contH);

            if (vRight <= vLeft || vBottom <= vTop) return null;

            const thumbScale = 0.2;
            const canvasScale = this.zoom / 100;
            const ow = this.pdfViewport.width;
            const oh = this.pdfViewport.height;

            let pLeft, pTop, pRight, pBottom;
            switch (this.rotation) {
                case 90:
                    pLeft = (oh - vBottom / canvasScale) * thumbScale;
                    pTop = (vLeft / canvasScale) * thumbScale;
                    pRight = (oh - vTop / canvasScale) * thumbScale;
                    pBottom = (vRight / canvasScale) * thumbScale;
                    break;
                case 180:
                    pLeft = (ow - vRight / canvasScale) * thumbScale;
                    pTop = (oh - vBottom / canvasScale) * thumbScale;
                    pRight = (ow - vLeft / canvasScale) * thumbScale;
                    pBottom = (oh - vTop / canvasScale) * thumbScale;
                    break;
                case 270:
                    pLeft = (vTop / canvasScale) * thumbScale;
                    pTop = (ow - vRight / canvasScale) * thumbScale;
                    pRight = (vBottom / canvasScale) * thumbScale;
                    pBottom = (ow - vLeft / canvasScale) * thumbScale;
                    break;
                default:
                    pLeft = (vLeft / canvasScale) * thumbScale;
                    pTop = (vTop / canvasScale) * thumbScale;
                    pRight = (vRight / canvasScale) * thumbScale;
                    pBottom = (vBottom / canvasScale) * thumbScale;
            }

            const indW = pRight - pLeft;
            const indH = pBottom - pTop;
            const natW = this.thumbnailWidth;
            const natH = this.thumbnailHeight;
            const dispScale = Math.min(120 / natW, 80 / natH);
            const offX = (120 - natW * dispScale) / 2;
            const offY = (80 - natH * dispScale) / 2;

            return {
                left: offX + pLeft * dispScale + 'px',
                top: offY + pTop * dispScale + 'px',
                width: indW * dispScale + 'px',
                height: indH * dispScale + 'px',
            };
        },
        thumbWrapperStyle() {
            if (!this.thumbnailWidth || !this.thumbnailHeight) return {};
            const natW = this.thumbnailWidth;
            const natH = this.thumbnailHeight;
            const dispScale = Math.min(120 / natW, 80 / natH);
            return {
                width: natW * dispScale + 'px',
                height: natH * dispScale + 'px',
            };
        },
    },
    watch: {
        viewMode(val) {
            if (val) {
                this.activeTool = 'hand';
            }
        },
        baseImageUrl(url) {
            if (url) this.loadPdf(url);
        },
        currentPage() {
            if (!this.pdfDoc) return;
            if (this.currentPage < 1 || this.currentPage > this.pdfDoc.numPages) return;
            this.translateX = 0;
            this.translateY = 0;
            this.rotation = 0;
            this.renderPage();
            this.emitCacheSync();
        },
        zoom() {
            this.renderPage();
        },
        activeAnnoId(id) {
            this.selectedAnnoId = id || '';
        },
        annotations: {
            deep: true,
            handler() {
                this.emitCacheSync();
            },
        },
        shapes: {
            deep: true,
            handler() {
                this.emitCacheSync();
            },
        },
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
        if (this.baseImageUrl) {
            this.loadPdf(this.baseImageUrl);
        }
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);
    },
    methods: {
        getSaveData() {
            const now = new Date().toISOString();
            const comments = [];

            this.annotations.forEach(anno => {
                if (anno._historical) return;
                const position = JSON.stringify({
                    type: "comment",
                    x: anno.x,
                    y: anno.y,
                    popupOffsetX: anno.popupOffsetX,
                    popupOffsetY: anno.popupOffsetY
                });
                comments.push({
                    content: anno.content || "-",
                    createAt: now,
                    createBy: this.userId,
                    createName: this.userName,
                    position,
                    title: anno.title || "-",
                    angle: this.rotation
                });
            });

            this.shapes.forEach(shape => {
                if (shape._historical) return;
                const s = { ...shape };
                delete s._historical;
                const position = JSON.stringify({
                    type: "shape",
                    shapeType: shape.type,
                    data: s
                });
                comments.push({
                    content: "-",
                    createAt: now,
                    createBy: this.userId,
                    createName: this.userName,
                    position,
                    title: "-",
                    angle: this.rotation
                });
            });

            return {
                pageComments: [{
                    pageId: this.pageId,
                    comments,
                }]
            };
        },
        isNewAnno(anno) {
            if (!anno) return false;
            if (anno._historical) return false;
            return typeof anno === 'string' ? anno.startsWith('anno_') : (anno.id && anno.id.startsWith('anno_'));
        },
        emitCacheSync() {
            this.$emit('cache-sync', this.pageId, this.annotations, this.shapes, this.rotation);
        },
        clearAll() {
            this.annotations = [];
            this.shapes = [];
            this.redrawAllShapes();
        },
        clearPdf() {
            if (this._pdfLoadingTask) {
                this._pdfLoadingTask.destroy();
                this._pdfLoadingTask = null;
            }
            if (this._renderTask) {
                this._renderTask.cancel();
                this._renderTask = null;
            }
            this.pdfDoc = null;
            this.pdfViewport = null;
            this.thumbnailUrl = "";
            this.translateX = 0;
            this.translateY = 0;
            this.rotation = 0;
            this.$emit("update:totalPages", 0);
        },
        loadHistoricalData(textAnnos, shapeList, rotation) {
            if (rotation !== undefined) this.rotation = rotation;
            this.annotations = (textAnnos || []).map(item => ({
                id: item.id,
                title: item.title === '-' ? '' : (item.title || ''),
                content: item.content === '-' ? '' : (item.content || ''),
                author: item.creator || '',
                createBy: item.createBy || '',
                createAt: item.createAt || '',
                createName: item.createName || '',
                x: item.x || 0,
                y: item.y || 0,
                popupOffsetX: item.popupOffsetX || '30px',
                popupOffsetY: item.popupOffsetY || '-10px',
                status: item.status || '',
                replyContent: item.replyContent || '',
                replyBy: item.replyBy || '',
                replyName: item.replyName || '',
                replyAt: item.replyAt || '',
                _historical: true,
            }));
            this.shapes = (shapeList || []).filter(s => s && s.type);
            if (this.pdfDoc) {
                this.renderPage();
            } else {
                this.redrawAllShapes();
            }
        },
        togglePopup(id) {
            const anno = this.annotations.find(a => a.id === id);
            if (!anno) return;
            this.activePopup = this.activePopup === id ? null : id;
            this.selectedAnnoId = id;
            this.$emit('select-anno', id, anno.replyContent || '');
        },
        removeAnnotation(id) {
            this.annotations = this.annotations.filter(a => a.id !== id);
            if (this.selectedAnnoId === id) this.selectedAnnoId = '';
            if (this.activePopup === id) this.activePopup = null;
        },
        updateAnnotation(id, title, content) {
            const anno = this.annotations.find(a => a.id === id);
            if (anno) {
                if (title !== undefined) anno.title = title;
                if (content !== undefined) anno.content = content;
            }
        },
        updateAnnotationReply(id, content) {
            const anno = this.annotations.find(a => a.id === id);
            if (anno) {
                anno.replyContent = content;
            }
        },
        updateAnnotationStatus(id, status) {
            const anno = this.annotations.find(a => a.id === id);
            if (anno) {
                anno.status = status;
            }
        },
        selectTool(tool) {
            this.activeTool = tool;
            this.selectedShapeIndex = -1;
            this.selectedAnnoId = '';
        },
        startReply(anno) {
            this.replyingAnnoId = anno.id;
            this.replyCache = anno.replyContent || '';
        },
        saveReply(anno) {
            if (!this.replyCache.trim()) return;
            anno.replyContent = this.replyCache;
            this.replyingAnnoId = '';
            this.replyCache = '';
            this.$emit('reply', { id: anno.id, content: anno.replyContent });
        },
        cancelReply() {
            this.replyingAnnoId = '';
            this.replyCache = '';
        },
        editReply(anno) {
            this.startReply(anno);
        },
        onReplyInput(anno) {
            this.$emit('reply', { id: anno.id, content: this.replyCache });
        },
        onColorChange(e) {
            const color = e.target.value;
            this.currentColor = color;
            if (this.selectedShapeIndex >= 0 && this.shapes[this.selectedShapeIndex]) {
                this.shapes[this.selectedShapeIndex].color = color;
                this.redrawAllShapes();
            }
        },

        // 将PDF逻辑坐标（scale=1, rotation=0）转换为画布像素坐标
        toCanvasCoords(x, y) {
            const s = this.scale;
            const ow = this.pdfViewport ? this.pdfViewport.width : 1;
            const oh = this.pdfViewport ? this.pdfViewport.height : 1;
            switch (this.rotation) {
                case 90:  return { x: (oh - y) * s, y: x * s };
                case 180: return { x: (ow - x) * s, y: (oh - y) * s };
                case 270: return { x: y * s, y: (ow - x) * s };
                default:  return { x: x * s, y: y * s };
            }
        },

        // 将画布像素坐标转换为PDF逻辑坐标（scale=1, rotation=0）
        toLogicalCoords(cx, cy) {
            const s = this.scale;
            const ow = this.pdfViewport ? this.pdfViewport.width : 1;
            const oh = this.pdfViewport ? this.pdfViewport.height : 1;
            switch (this.rotation) {
                case 90:  return { x: cy / s, y: oh - cx / s };
                case 180: return { x: ow - cx / s, y: oh - cy / s };
                case 270: return { x: ow - cy / s, y: cx / s };
                default:  return { x: cx / s, y: cy / s };
            }
        },

        getShapeAtPoint(pdfX, pdfY) {
            const threshold = 12 / this.scale;
            for (let i = this.shapes.length - 1; i >= 0; i--) {
                const s = this.shapes[i];
                if (s.type === 'circle') {
                    const d = Math.sqrt((pdfX - s.x) ** 2 + (pdfY - s.y) ** 2);
                    if (Math.abs(d - s.radius) < threshold) return i;
                } else if (s.type === 'rectangle') {
                    const x1 = Math.min(s.x, s.x + s.w), x2 = Math.max(s.x, s.x + s.w);
                    const y1 = Math.min(s.y, s.y + s.h), y2 = Math.max(s.y, s.y + s.h);
                    if (pdfX >= x1 - threshold && pdfX <= x2 + threshold && pdfY >= y1 - threshold && pdfY <= y2 + threshold) return i;
                } else if (s.type === 'arrow') {
                    if (this.distToSegment(pdfX, pdfY, s.x1, s.y1, s.x2, s.y2) < threshold) return i;
                } else if (s.type === 'pen' && s.points) {
                    for (let j = 1; j < s.points.length; j++) {
                        if (this.distToSegment(pdfX, pdfY, s.points[j-1].x, s.points[j-1].y, s.points[j].x, s.points[j].y) < threshold) return i;
                    }
                }
            }
            return -1;
        },
        distToSegment(px, py, x1, y1, x2, y2) {
            const dx = x2 - x1, dy = y2 - y1;
            const lenSq = dx * dx + dy * dy;
            if (lenSq === 0) return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
            let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
            t = Math.max(0, Math.min(1, t));
            return Math.sqrt((px - (x1 + t * dx)) ** 2 + (py - (y1 + t * dy)) ** 2);
        },
        startMarkerDrag(anno, e) {
            if (!this.isNewAnno(anno)) { this.togglePopup(anno.id); return; }
            e.preventDefault();
            const pos = this.getPdfCoordinates(e);
            this.draggingAnno = anno;
            this.selectedAnnoId = anno.id;
            this.selectedShapeIndex = -1;
            this.dragStartPdf = { x: anno.x, y: anno.y };
            this.mouseStart = { x: pos.x, y: pos.y };
            this.isClick = true;

            const onMove = (ev) => {
                const p = this.getPdfCoordinates(ev);
                const dx = p.x - this.mouseStart.x;
                const dy = p.y - this.mouseStart.y;
                if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.isClick = false;
                const vpw = this.pdfViewport ? this.pdfViewport.width : 1;
                const vph = this.pdfViewport ? this.pdfViewport.height : 1;
                anno.x = Math.max(0, Math.min(100, this.dragStartPdf.x + (dx / vpw) * 100));
                anno.y = Math.max(0, Math.min(100, this.dragStartPdf.y + (dy / vph) * 100));
            };

            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                if (this.isClick) this.togglePopup(anno.id);
                this.draggingAnno = null;
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        async deleteSelected() {
            if (this.selectedShapeIndex >= 0) {
                const shape = this.shapes[this.selectedShapeIndex];
                if (shape && shape.id && !String(shape.id).startsWith('shape_') && !this.isNewAnno({ id: shape.id })) {
                    try {
                        await deleteAuditMarkupComment(shape.id);
                    } catch (e) {
                        console.error('删除批注失败', e);
                    }
                }
                this.shapes.splice(this.selectedShapeIndex, 1);
                this.selectedShapeIndex = -1;
                this.redrawAllShapes();
            } else if (this.selectedAnnoId) {
                const anno = this.annotations.find(a => a.id === this.selectedAnnoId);
                if (anno && anno.id && !this.isNewAnno(anno)) {
                    try {
                        await deleteAuditMarkupComment(anno.id);
                    } catch (e) {
                        console.error('删除批注失败', e);
                    }
                }
                this.annotations = this.annotations.filter(a => a.id !== this.selectedAnnoId);
                this.selectedAnnoId = '';
                this.activePopup = null;
            }
        },
        handleKeydown(e) {
            // 焦点在输入框/文本域内时，Delete/Backspace 应正常删除文字，不能误删整个批注
            const target = e.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (this.selectedShapeIndex >= 0 || this.selectedAnnoId) {
                    e.preventDefault();
                    this.deleteSelected();
                }
            }
            if (e.key === 'Escape') {
                this.selectedShapeIndex = -1;
                this.selectedAnnoId = '';
            }
        },
        undo() {
            if (this.shapes.length > 0) {
                this.shapes.pop();
                this.redrawAllShapes();
            }
        },

        rotateCycle() {
            this.rotation = (this.rotation + 90) % 360;
            this.renderPage();
            this.emitCacheSync();
        },

        resetRotation() {
            this.rotation = 0;
            this.renderPage();
            this.emitCacheSync();
        },

        // 缩略图视口框拖拽平移
        onThumbIndicatorDown(e) {
            this.thumbDragging = true;
            this.thumbDragStart = { x: e.clientX, y: e.clientY };
            this.thumbTranslateStart = { x: this.translateX, y: this.translateY };
            document.addEventListener('mousemove', this.onThumbDrag);
            document.addEventListener('mouseup', this.onThumbDragEnd);
        },

        onThumbDrag(e) {
            if (!this.thumbDragging) return;
            const dx = e.clientX - this.thumbDragStart.x;
            const dy = e.clientY - this.thumbDragStart.y;
            const natW = this.thumbnailWidth;
            const natH = this.thumbnailHeight;
            const canvasScale = this.zoom / 100;
            const dispScale = Math.min(120 / natW, 80 / natH);
            const ratio = canvasScale / (dispScale * 0.2);
            this.translateX = this.thumbTranslateStart.x + dx * ratio;
            this.translateY = this.thumbTranslateStart.y + dy * ratio;
            this.redrawAllShapes();
        },

        onThumbDragEnd() {
            this.thumbDragging = false;
            document.removeEventListener('mousemove', this.onThumbDrag);
            document.removeEventListener('mouseup', this.onThumbDragEnd);
        },

        // 缩略图视口框边缘拖拽缩放
        onThumbResizeStart(e, dir) {
            this.thumbResizing = true;
            this.thumbResizeDir = dir;
            this.thumbResizeStart = { x: e.clientX, y: e.clientY, zoom: this.zoom, translateX: this.translateX, translateY: this.translateY };
            document.addEventListener('mousemove', this.onThumbResize);
            document.addEventListener('mouseup', this.onThumbResizeEnd);
        },

        onThumbResize(e) {
            if (!this.thumbResizing) return;
            const dx = e.clientX - this.thumbResizeStart.x;
            const dy = e.clientY - this.thumbResizeStart.y;
            const dir = this.thumbResizeDir;
            let delta = 0;
            if (dir.includes('e')) delta += dx;
            if (dir.includes('w')) delta -= dx;
            if (dir.includes('s')) delta += dy;
            if (dir.includes('n')) delta -= dy;
            const oldZoom = this.thumbResizeStart.zoom;
            const newZoom = Math.max(10, Math.min(500, oldZoom + delta));
            this.adjustTranslateForZoom(oldZoom, newZoom);
            this.$emit("update:zoom", newZoom);
        },

        onThumbResizeEnd() {
            this.thumbResizing = false;
            document.removeEventListener('mousemove', this.onThumbResize);
            document.removeEventListener('mouseup', this.onThumbResizeEnd);
        },

        getPdfCoordinates(e) {
            const container = this.$refs.canvasContainer;
            if (!container) return { x: 0, y: 0 };
            const rect = container.getBoundingClientRect();
            const containerX = e.clientX - rect.left;
            const containerY = e.clientY - rect.top;
            const canvasX = containerX - this.translateX;
            const canvasY = containerY - this.translateY;
            return this.toLogicalCoords(canvasX, canvasY);
        },

        handleMouseDown(e) {
            if (this.activeTool === 'hand') {
                const pos = this.getPdfCoordinates(e);
                const shapeIdx = this.getShapeAtPoint(pos.x, pos.y);
                if (shapeIdx >= 0) {
                    this.selectedShapeIndex = shapeIdx;
                    this.selectedAnnoId = '';
                    this.currentColor = this.shapes[shapeIdx].color || this.currentColor;
                    return;
                }
                this.selectedShapeIndex = -1;
                this.selectedAnnoId = '';
                this.isDragging = true;
                this.dragStartX = e.clientX - this.translateX;
                this.dragStartY = e.clientY - this.translateY;
                return;
            }

            if (this.activeTool === 'comment') {
                const pos = this.getPdfCoordinates(e);
                const xPercent = this.pdfViewport ? (pos.x / this.pdfViewport.width) * 100 : 0;
                const yPercent = this.pdfViewport ? (pos.y / this.pdfViewport.height) * 100 : 0;

                const newAnno = {
                    id: 'anno_' + Date.now(),
                    title: '',
                    content: '',
                    author: this.userName || '李设计师',
                    creator: this.userName || '当前用户',
                    createName: this.userName || '当前用户',
                    x: xPercent,
                    y: yPercent,
                    popupOffsetX: '30px',
                    popupOffsetY: '-10px'
                };
                this.annotations.push(newAnno);
                this.activePopup = newAnno.id;
                this.activeTool = 'hand';
                this.$emit('add-annotation', newAnno);
                return;
            }

            if (['circle', 'rectangle', 'arrow'].includes(this.activeTool)) {
                const pos = this.getPdfCoordinates(e);
                this.isDrawing = true;
                this.drawStartX = pos.x;
                this.drawStartY = pos.y;
            } else if (this.activeTool === 'pen') {
                const pos = this.getPdfCoordinates(e);
                this.isDrawing = true;
                this.currentPoints = [{ x: pos.x, y: pos.y }];
            }
        },

        handleMouseMove(e) {
            if (this.activeTool === 'hand' && this.isDragging) {
                this.translateX = e.clientX - this.dragStartX;
                this.translateY = e.clientY - this.dragStartY;
                return;
            }
            if (!this.isDrawing) return;

            const pos = this.getPdfCoordinates(e);
            const canvas = this.$refs.drawCanvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');

            this.redrawAllShapes();
            ctx.strokeStyle = this.currentColor;
            ctx.lineWidth = 2;

            const start = this.toCanvasCoords(this.drawStartX, this.drawStartY);
            const cur = this.toCanvasCoords(pos.x, pos.y);

            if (this.activeTool === 'circle') {
                const radius = Math.sqrt(Math.pow(cur.x - start.x, 2) + Math.pow(cur.y - start.y, 2));
                ctx.beginPath();
                ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
                ctx.stroke();
            } else if (this.activeTool === 'rectangle') {
                ctx.strokeRect(start.x, start.y, cur.x - start.x, cur.y - start.y);
            } else if (this.activeTool === 'arrow') {
                this.drawArrow(ctx, start.x, start.y, cur.x, cur.y);
            } else if (this.activeTool === 'pen') {
                this.currentPoints.push({ x: pos.x, y: pos.y });
                ctx.beginPath();
                const first = this.toCanvasCoords(this.currentPoints[0].x, this.currentPoints[0].y);
                ctx.moveTo(first.x, first.y);
                for (let i = 1; i < this.currentPoints.length; i++) {
                    const pt = this.toCanvasCoords(this.currentPoints[i].x, this.currentPoints[i].y);
                    ctx.lineTo(pt.x, pt.y);
                }
                ctx.stroke();
            }
        },

        handleMouseUp(e) {
            if (this.activeTool === 'hand') {
                this.isDragging = false;
                return;
            }
            if (!this.isDrawing) return;
            this.isDrawing = false;

            const color = this.currentColor;
            const pos = this.getPdfCoordinates(e);
            if (this.activeTool === 'pen') {
                this.shapes.push({ type: 'pen', color, points: [...this.currentPoints] });
            } else if (this.activeTool === 'circle') {
                const dx = pos.x - this.drawStartX;
                const dy = pos.y - this.drawStartY;
                const radius = Math.sqrt(dx * dx + dy * dy);
                this.shapes.push({ type: 'circle', color, x: this.drawStartX, y: this.drawStartY, radius });
            } else if (this.activeTool === 'rectangle') {
                this.shapes.push({ type: 'rectangle', color, x: this.drawStartX, y: this.drawStartY, w: pos.x - this.drawStartX, h: pos.y - this.drawStartY });
            } else if (this.activeTool === 'arrow') {
                this.shapes.push({ type: 'arrow', color, x1: this.drawStartX, y1: this.drawStartY, x2: pos.x, y2: pos.y });
            }
            this.selectedShapeIndex = this.shapes.length - 1;
            this.currentPoints = [];
            this.redrawAllShapes();
        },

        drawArrow(ctx, fromX, fromY, toX, toY) {
            const headlen = 10;
            const dx = toX - fromX;
            const dy = toY - fromY;
            const angle = Math.atan2(dy, dx);
            ctx.beginPath();
            ctx.moveTo(fromX, fromY);
            ctx.lineTo(toX, toY);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(toX, toY);
            ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
            ctx.moveTo(toX, toY);
            ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
            ctx.stroke();
        },

        redrawAllShapes() {
            const canvas = this.$refs.drawCanvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            // 画布实际分辨率放大了 dpr 倍，缩放上下文使绘制坐标保持 CSS 像素
            const dpr = window.devicePixelRatio || 1;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            this.shapes.forEach((shape, idx) => {
                const color = shape.color || '#ff0000';
                const isSelected = idx === this.selectedShapeIndex;

                ctx.strokeStyle = color;
                ctx.lineWidth = isSelected ? 4 : 2;

                if (shape.type === 'circle') {
                    const pos = this.toCanvasCoords(shape.x, shape.y);
                    const r = shape.radius * this.scale;
                    ctx.beginPath();
                    ctx.arc(pos.x, pos.y, r, 0, 2 * Math.PI);
                    ctx.stroke();
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.stroke();
                        ctx.setLineDash([]);
                    }
                } else if (shape.type === 'rectangle') {
                    const p1 = this.toCanvasCoords(shape.x, shape.y);
                    const p2 = this.toCanvasCoords(shape.x + shape.w, shape.y + shape.h);
                    ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
                        ctx.setLineDash([]);
                    }
                } else if (shape.type === 'arrow') {
                    const p1 = this.toCanvasCoords(shape.x1, shape.y1);
                    const p2 = this.toCanvasCoords(shape.x2, shape.y2);
                    this.drawArrow(ctx, p1.x, p1.y, p2.x, p2.y);
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        this.drawArrow(ctx, p1.x, p1.y, p2.x, p2.y);
                        ctx.setLineDash([]);
                    }
                } else if (shape.type === 'pen') {
                    ctx.beginPath();
                    const first = this.toCanvasCoords(shape.points[0].x, shape.points[0].y);
                    ctx.moveTo(first.x, first.y);
                    for (let i = 1; i < shape.points.length; i++) {
                        const pt = this.toCanvasCoords(shape.points[i].x, shape.points[i].y);
                        ctx.lineTo(pt.x, pt.y);
                    }
                    ctx.stroke();
                    if (isSelected) {
                        ctx.strokeStyle = '#1890ff';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([5, 5]);
                        ctx.beginPath();
                        ctx.moveTo(first.x, first.y);
                        for (let i = 1; i < shape.points.length; i++) {
                            const pt = this.toCanvasCoords(shape.points[i].x, shape.points[i].y);
                            ctx.lineTo(pt.x, pt.y);
                        }
                        ctx.stroke();
                        ctx.setLineDash([]);
                    }
                }
            });
        },

        getAnnotationStyle(anno) {
            if (!this.pdfViewport) {
                return { left: '0%', top: '0%' };
            }
            const logicalX = (anno.x / 100) * this.pdfViewport.width;
            const logicalY = (anno.y / 100) * this.pdfViewport.height;
            const pos = this.toCanvasCoords(logicalX, logicalY);
            return {
                left: pos.x + 'px',
                top: pos.y + 'px',
            };
        },

        handleMouseWheel(e) {
            if (e.deltaY < 0) {
                this.zoomIn();
            } else {
                this.zoomOut();
            }
        },

        zoomIn() {
            if (this.zoom >= 500) return;
            const oldZoom = this.zoom;
            const newZoom = oldZoom + 10;
            this.adjustTranslateForZoom(oldZoom, newZoom);
            this.$emit("update:zoom", newZoom);
        },

        zoomOut() {
            if (this.zoom <= 10) return;
            const oldZoom = this.zoom;
            const newZoom = oldZoom - 10;
            this.adjustTranslateForZoom(oldZoom, newZoom);
            this.$emit("update:zoom", newZoom);
        },

        adjustTranslateForZoom(oldZoom, newZoom) {
            const container = this.$refs.canvasContainer;
            if (!container) return;
            const centerX = container.offsetWidth / 2;
            const centerY = container.offsetHeight / 2;
            const oldScale = oldZoom / 100;
            const pointX = (centerX - this.translateX) / oldScale;
            const pointY = (centerY - this.translateY) / oldScale;
            const newScale = newZoom / 100;
            this.translateX = centerX - pointX * newScale;
            this.translateY = centerY - pointY * newScale;
        },

        prevPage() { if (this.currentPage > 1) this.$emit("update:currentPage", this.currentPage - 1); },
        nextPage() { if (this.currentPage < this.totalPages) this.$emit("update:currentPage", this.currentPage + 1); },

        loadPdf(url) {
            if (this._pdfLoadingTask) {
                this._pdfLoadingTask.destroy();
                this._pdfLoadingTask = null;
            }
            this.pdfDoc = null;
            if (this._renderTask) {
                this._renderTask.cancel();
                this._renderTask = null;
            }
            this.thumbnailUrl = "";
            this.translateX = 0;
            this.translateY = 0;
            this.rotation = 0;
            this._pdfLoadingTask = window.pdfjsLib.getDocument(url);
            this._pdfLoadingTask.promise.then((pdf) => {
                this.pdfDoc = pdf;
                this.$emit("update:totalPages", pdf.numPages);
                this.generateThumbnail();
                if (this.currentPage > pdf.numPages) {
                    this.$emit("update:currentPage", 1);
                } else {
                    this.renderPage();
                }
            }).catch((err) => {
                if (err?.name !== 'RenderingCancelledException') {
                    console.error("pdfjs load error", err);
                }
            });
        },

        generateThumbnail() {
            if (!this.pdfDoc) return;
            this.pdfDoc.getPage(1).then((page) => {
                const dpr = window.devicePixelRatio || 1;
                const scale = 0.2;
                const viewport = page.getViewport({ scale });
                const renderViewport = page.getViewport({ scale: scale * dpr });
                const canvas = document.createElement("canvas");
                canvas.width = renderViewport.width;
                canvas.height = renderViewport.height;
                this.thumbnailWidth = viewport.width;
                this.thumbnailHeight = viewport.height;
                const ctx = canvas.getContext("2d");
                page.render({ canvasContext: ctx, viewport: renderViewport }).promise.then(() => {
                    this.thumbnailUrl = canvas.toDataURL("image/png");
                });
            });
        },

        renderPage() {
            if (!this.pdfDoc) return;
            const pdfCanvas = this.$refs.pdfCanvas;
            const drawCanvas = this.$refs.drawCanvas;
            if (!pdfCanvas || !drawCanvas) return;

            if (this._renderTask) {
                this._renderTask.cancel();
                this._renderTask = null;
            }

            const scale = this.zoom / 100;

            this.pdfDoc.getPage(this.currentPage).then((page) => {
                // pdf.js 的 getViewport({ rotation }) 会用传入值“替换”页面自带的 /Rotate（绝对旋转，非叠加）。
                // rotation=0（默认）必须按页面 /Rotate 正常显示；用户的 this.rotation 是在正常方向上的附加旋转。
                const intrinsicRotation = (((page.rotate || 0) % 360) + 360) % 360;
                const rotation = (this.rotation + intrinsicRotation) % 360;
                this.pdfViewport = page.getViewport({ scale: 1, rotation: intrinsicRotation });

                // 按 devicePixelRatio 放大画布实际分辨率，避免高分屏下模糊；CSS 尺寸保持逻辑大小
                // 缩小时按不低于基准分辨率渲染、再由 CSS 缩小显示，避免直接低分辨率渲染导致文字发虚
                const dpr = window.devicePixelRatio || 1;
                let renderScale = Math.max(scale, 1) * dpr;
                // 防止超大缩放下画布尺寸超出浏览器限制导致渲染失败
                const maxScale = Math.min(16384 / this.pdfViewport.width, 16384 / this.pdfViewport.height, Math.sqrt(134217728 / (this.pdfViewport.width * this.pdfViewport.height)));
                if (renderScale > maxScale) renderScale = maxScale;
                const viewport = page.getViewport({ scale: scale, rotation });
                const renderViewport = page.getViewport({ scale: renderScale, rotation });

                pdfCanvas.width = renderViewport.width;
                pdfCanvas.height = renderViewport.height;
                pdfCanvas.style.width = viewport.width + 'px';
                pdfCanvas.style.height = viewport.height + 'px';
                // 绘制层位图按 CSS 尺寸 × dpr 设置：缩小时 PDF 层以不低于基准分辨率渲染再由 CSS 缩小显示，
                // 若绘制层跟随 renderViewport，其位图与 CSS 尺寸不再是 dpr 倍关系，redrawAllShapes 的 dpr 变换
                // 会导致缩小时绘制内容偏离鼠标位置（放大时 renderScale = scale * dpr，二者恰好一致所以正常）
                drawCanvas.width = viewport.width * dpr;
                drawCanvas.height = viewport.height * dpr;
                drawCanvas.style.width = viewport.width + 'px';
                drawCanvas.style.height = viewport.height + 'px';

                const ctx = pdfCanvas.getContext("2d");
                this._renderTask = page.render({ canvasContext: ctx, viewport: renderViewport });
                this._renderTask.promise.then(() => {
                    this._renderTask = null;
                    this.centerCanvas();
                    this.redrawAllShapes();
                }).catch((err) => {
                    if (err?.name !== 'RenderingCancelledException') {
                        console.error("render error", err);
                    }
                });
            });
        },

        centerCanvas() {
            const container = this.$refs.canvasContainer;
            const canvas = this.$refs.pdfCanvas;
            if (!container || !canvas) return;
            const dpr = window.devicePixelRatio || 1;
            this.translateX = (container.offsetWidth - canvas.width / dpr) / 2;
            this.translateY = (container.offsetHeight - canvas.height / dpr) / 2;
        },
    }
};
</script>

<style lang="less" scoped>
.annotation-viewer {
    position: relative;
    width: 100%;
    height: 100vh;
    background: #f0f0f0;
    overflow: hidden;
}

.toolbar {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    padding: 8px 16px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    z-index: 10;

    .separator {
        width: 1px;
        height: 24px;
        background: #e0e0e0;
        margin: 0 4px;
    }

    .color-label {
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 2px;
        border: 2px solid #ddd;
        border-radius: 4px;
        &:hover { border-color: #1890ff; }
    }

    .color-picker {
        width: 22px;
        height: 22px;
        border: none;
        padding: 0;
        cursor: pointer;
    }

    .tool-btn {
        border: none;
        background: transparent;
        font-size: 20px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        &:hover {
            background: #f0f0f0;
        }
        &.active{
            background: #e6f7ff;
        }
        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }
    .view-mode-tip {
        font-size: 14px;
        color: #999;
        padding: 4px 12px;
    }
}

.canvas-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #e9e9e9;
    cursor: default;

    &.hand-tool {
        cursor: grab;
        &:active {
            cursor: grabbing;
        }
    }

    .transform-layer {
        position: absolute;
        top: 0;
        left: 0;
        transform-origin: 0 0;
    }

    .base-image {
        position: absolute;
        border: none;
        top: 0;
        left: 0;
    }

    .draw-canvas {
        position: absolute;
        border: none;
        top: 0;
        left: 0;
        pointer-events: none;
    }
}

.annotation-marker {
    position: absolute;
    cursor: pointer;
    z-index: 5;

    &.marker-new .marker-icon {
        cursor: move;
    }

    &.marker-selected > .marker-icon {
        box-shadow: 0 0 0 3px #1890ff;
    }

    &.marker-end .marker-icon {
        background: #52c41a;
    }

    .marker-icon {
        width: 32px;
        height: 32px;
        background: #ff4d4f;
        border-radius: 4px;
        position: relative;
        &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 16px;
            height: 16px;
            background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>') center/cover;
        }
    }

    .popup-card {
        position: absolute;
        top: 0;
        left: 0;
        width: 260px;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        padding: 12px;
        display: none;
        z-index:9999999;
        &.active {
            display: block;
        }

        .popup-header {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            gap: 8px;

            .popup-title-input {
                flex: 1;
                border: none;
                font-weight: 600;
                font-size: 14px;
                outline: none;
                padding: 0;
            }
            .close-btn {
                border: none;
                background: transparent;
                font-size: 18px;
                cursor: pointer;
                color: #999;
                flex-shrink: 0;
            }
        }

        .popup-content {
            margin-bottom: 12px;

            .popup-content-textarea {
                width: 100%;
                border: 1px solid #eee;
                border-radius: 4px;
                padding: 8px;
                font-size: 13px;
                color: #666;
                line-height: 1.5;
                outline: none;
                resize: vertical;
                min-height: 60px;
                box-sizing: border-box;
            }
        }

        .reply-input-area {
            margin-bottom: 8px;

            .reply-textarea {
                width: 100%;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                padding: 6px 8px;
                font-size: 12px;
                resize: vertical;
                min-height: 50px;
                outline: none;
                box-sizing: border-box;
                &:focus { border-color: #1890ff; }
            }

            .reply-actions {
                display: flex;
                gap: 8px;
                margin-top: 6px;

                .save-reply-btn {
                    flex: 1;
                    border: none;
                    background: #1890ff;
                    color: #fff;
                    padding: 4px 0;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    &:hover { background: #40a9ff; }
                }

                .cancel-reply-btn {
                    flex: 1;
                    border: 1px solid #d9d9d9;
                    background: #fff;
                    color: #666;
                    padding: 4px 0;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    &:hover { border-color: #1890ff; color: #1890ff; }
                }
            }
        }

        .popup-reply {
            margin-bottom: 8px;
            padding: 6px 8px;
            background: #f6ffed;
            border: 1px solid #b7eb8f;
            border-radius: 4px;
            font-size: 12px;
            line-height: 1.5;
            .reply-label { color: #52c41a; font-weight: 500; }
            .reply-text { color: #333; }
        }

        .popup-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .author {
                font-size: 12px;
                color: #999;
            }

            .reply-btn {
                font-size: 12px;
                color: #1890ff;
                cursor: pointer;
                &:hover { color: #40a9ff; }
            }

            .edit-reply-btn {
                font-size: 12px;
                color: #1890ff;
                cursor: pointer;
                &:hover { color: #40a9ff; }
            }
        }
    }
}

.zoom-controls {
    position: absolute;
    right: 20px;
    bottom: 70px;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);

    .zoom-btn {
        border: none;
        background: #fff;
        padding: 8px 12px;
        font-size: 18px;
        cursor: pointer;
        &:hover {
            background: #f0f0f0;
        }
        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }
    .zoom-level {
        text-align: center;
        padding: 4px;
        font-size: 12px;
        color: #666;
    }
    .zoom-separator {
        height: 1px;
        background: #e0e0e0;
        margin: 4px 8px;
    }
    .rotate-icon {
        display: inline-block;
        transition: transform 0.2s ease;
    }
}

.thumbnail-container {
    position: absolute;
    left: 20px;
    bottom: 70px;
    width: 120px;
    height: 80px;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
    justify-content: center;

    .thumb-wrapper {
        position: relative;
        overflow: hidden;

        .thumbnail {
            width: 100%;
            height: 100%;
            display: block;
        }

        .thumb-indicator {
            position: absolute;
            top: 0;
            left: 0;
            border: 2px solid rgba(255, 60, 60, 0.85);
            background: rgba(255, 60, 60, 0.12);
            cursor: move;
            box-sizing: border-box;
            z-index: 2;

            .thumb-resize-handle {
                position: absolute;
                width: 8px;
                height: 8px;
                background: #ff3c3c;
                border: 1px solid #fff;
                border-radius: 50%;

                &.thumb-resize-nw { top: -4px; left: -4px; cursor: nw-resize; }
                &.thumb-resize-ne { top: -4px; right: -4px; cursor: ne-resize; }
                &.thumb-resize-sw { bottom: -4px; left: -4px; cursor: sw-resize; }
                &.thumb-resize-se { bottom: -4px; right: -4px; cursor: se-resize; }
                &.thumb-resize-n  { top: -4px; left: 50%; margin-left: -4px; cursor: n-resize; }
                &.thumb-resize-s  { bottom: -4px; left: 50%; margin-left: -4px; cursor: s-resize; }
                &.thumb-resize-w  { top: 50%; left: -4px; margin-top: -4px; cursor: w-resize; }
                &.thumb-resize-e  { top: 50%; right: -4px; margin-top: -4px; cursor: e-resize; }
            }
        }
    }
}

.pagination {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 16px;
    background: rgba(0,0,0,0.7);
    color: #fff;
    padding: 6px 12px;
    border-radius: 6px;

    .page-btn {
        background: transparent;
        border: none;
        color: #fff;
        cursor: pointer;
        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }
    .page-info {
        font-size: 13px;
    }
}
</style>
